import type { BlogPost } from './blog-types';
import type { ServiceSeoPage } from './service-seo-types';
import {
  buildBlogIndexLdGraph,
  buildBlogPostLdGraph,
  buildContactLdGraph,
  buildPrivacyLdGraph,
  buildServiceSeoPageLdGraph,
  buildServicesHubLdGraph,
  buildTermsLdGraph,
} from './seo-jsonld';
import { clearSocialPreview, setSocialPreview } from './social-preview';
import { canonicalUrl, getSiteUrl } from './site-url';

const LD_SCRIPT_ID = 'ywc-structured-data';
const DEFAULT_TITLE = 'Yes We Can LLC';
const DEFAULT_DESCRIPTION =
  'Yes We Can LLC — software, web & mobile apps, immigration & visas, taxes, divorce, SNAP & FDA help, imports, design & business cards, POS, insurance, bankruptcy, admissions. We deliver or connect you with trusted partners.';

function upsertMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertRobots(content: string) {
  upsertMeta('robots', content);
}

function setJsonLd(data: object) {
  let script = document.getElementById(LD_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = LD_SCRIPT_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeJsonLd() {
  document.getElementById(LD_SCRIPT_ID)?.remove();
}

/** Home and other main pages: restore index.html defaults. */
export function applyDefaultDocumentSeo() {
  document.title = DEFAULT_TITLE;
  upsertMeta('description', DEFAULT_DESCRIPTION);
  upsertLink('canonical', getSiteUrl() + '/');
  upsertRobots('index, follow');
  removeJsonLd();
  setSocialPreview({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalUrl: getSiteUrl() + '/',
    ogType: 'website',
  });
}

export function applyAboutPageSeo() {
  const desc =
    'About Yes We Can LLC: software, web and mobile apps, immigration and visa guidance, taxes, design, imports, and more—or warm introductions to partners we trust when it is not our lane.';
  document.title = 'About — Yes We Can LLC';
  upsertMeta('description', desc);
  upsertLink('canonical', canonicalUrl('/about'));
  upsertRobots('index, follow');
  removeJsonLd();
  setSocialPreview({
    title: 'About — Yes We Can LLC',
    description: desc,
    canonicalUrl: canonicalUrl('/about'),
    ogType: 'website',
  });
}

export function applyContactPageSeo() {
  const desc =
    'Contact Yes We Can LLC: book a consult, email support@yeswecanhq.com, WhatsApp or call +1 (319) 296-5799, and Facebook. Based in Iowa, United States.';
  document.title = 'Contact — Yes We Can LLC';
  upsertMeta('description', desc);
  upsertLink('canonical', canonicalUrl('/contact'));
  upsertRobots('index, follow');
  setJsonLd(buildContactLdGraph());
  setSocialPreview({
    title: 'Contact — Yes We Can LLC',
    description: desc,
    canonicalUrl: canonicalUrl('/contact'),
    ogType: 'website',
  });
}

export function applyTermsPageSeo() {
  const desc =
    'Terms of use for Yes We Can LLC, including fees, refunds, cancellations, intellectual property, limitations of liability, and how site content relates to licensed professional advice.';
  document.title = 'Terms of Use — Yes We Can LLC';
  upsertMeta('description', desc);
  upsertLink('canonical', canonicalUrl('/terms'));
  upsertRobots('index, follow');
  setJsonLd(buildTermsLdGraph());
  setSocialPreview({
    title: 'Terms of Use — Yes We Can LLC',
    description: desc,
    canonicalUrl: canonicalUrl('/terms'),
    ogType: 'website',
  });
}

export function applyPrivacyPageSeo() {
  const desc =
    'Privacy policy for Yes We Can LLC: information we collect, how we use and retain it, your choices, international visitors, children’s privacy, and third-party services.';
  document.title = 'Privacy Policy — Yes We Can LLC';
  upsertMeta('description', desc);
  upsertLink('canonical', canonicalUrl('/privacy'));
  upsertRobots('index, follow');
  setJsonLd(buildPrivacyLdGraph());
  setSocialPreview({
    title: 'Privacy Policy — Yes We Can LLC',
    description: desc,
    canonicalUrl: canonicalUrl('/privacy'),
    ogType: 'website',
  });
}

export function applyBlogPostSeo(post: BlogPost) {
  const canon = canonicalUrl(`/blog/${post.slug}`);
  document.title = `${post.title} | Yes We Can LLC`;
  upsertMeta('description', post.metaDescription);
  upsertLink('canonical', canon);
  upsertRobots('index, follow');
  setJsonLd(buildBlogPostLdGraph(post));
  setSocialPreview({
    title: `${post.title} | Yes We Can LLC`,
    description: post.metaDescription,
    canonicalUrl: canon,
    ogType: 'article',
  });
}

export function applyBlogIndexSeo(page: number) {
  const path = page <= 1 ? '/blog' : `/blog?page=${page}`;
  const title =
    page <= 1 ? 'Blog — Yes We Can LLC' : `Blog (page ${page}) — Yes We Can LLC`;
  const desc =
    page <= 1
      ? 'Guides and overviews on visas, cross-border taxes, business setup, and relocation. Informational only—not legal or tax advice.'
      : `More articles (page ${page}): visas, taxes, business, and relocation. Informational only—not legal or tax advice.`;
  const canon = canonicalUrl(path);
  document.title = title;
  upsertMeta('description', desc);
  upsertLink('canonical', canon);
  upsertRobots('index, follow');
  setJsonLd(buildBlogIndexLdGraph(page));
  setSocialPreview({
    title,
    description: desc,
    canonicalUrl: canon,
    ogType: 'website',
  });
}

export function applyBlogNotFoundSeo(slug: string) {
  const desc = `We could not find a blog article for “${slug}”. Browse the blog for visas, taxes, and cross-border topics.`;
  const canon = canonicalUrl(`/blog/${slug}`);
  document.title = `Not found | Yes We Can LLC`;
  upsertMeta('description', desc);
  upsertLink('canonical', canon);
  upsertRobots('noindex, follow');
  removeJsonLd();
  clearSocialPreview();
}

export function applyServiceSeoPage(page: ServiceSeoPage) {
  const canon = canonicalUrl(`/services/${page.slug}`);
  document.title = page.title;
  upsertMeta('description', page.metaDescription);
  upsertLink('canonical', canon);
  upsertRobots('index, follow');
  setJsonLd(buildServiceSeoPageLdGraph(page));
  setSocialPreview({
    title: page.title,
    description: page.metaDescription,
    canonicalUrl: canon,
    ogType: 'website',
  });
}

export function applyServicesHubSeo(pageNum: number) {
  const path = pageNum <= 1 ? '/services' : `/services?page=${pageNum}`;
  const title =
    pageNum <= 1 ? 'Services — Yes We Can LLC' : `Services (page ${pageNum}) — Yes We Can LLC`;
  const desc =
    pageNum <= 1
      ? 'What Yes We Can LLC can help with: software, web and mobile apps, immigration-related coordination, taxes, design, imports, and more—or introductions to partners when you need them.'
      : `More service pages (page ${pageNum}).`;
  const canon = canonicalUrl(path);
  document.title = title;
  upsertMeta('description', desc);
  upsertLink('canonical', canon);
  upsertRobots('noindex, follow');
  setJsonLd(buildServicesHubLdGraph(pageNum));
  setSocialPreview({
    title,
    description: desc,
    canonicalUrl: canon,
    ogType: 'website',
  });
}

export function applyServiceNotFoundSeo(slug: string) {
  const desc = `No service page at “${slug}”. Browse all services or the home page.`;
  const canon = canonicalUrl(`/services/${slug}`);
  document.title = `Not found | Yes We Can LLC`;
  upsertMeta('description', desc);
  upsertLink('canonical', canon);
  upsertRobots('noindex, follow');
  removeJsonLd();
  clearSocialPreview();
}

/** Unknown site path (SPA catch-all): keep URL, tell crawlers not to index. */
export function applyGlobalNotFoundSeo(requestPath: string) {
  const p = requestPath.startsWith('/') ? requestPath : `/${requestPath}`;
  const desc = `We could not find a page at “${p}”. Try the home page, blog, or services directory.`;
  document.title = 'Page not found — Yes We Can LLC';
  upsertMeta('description', desc);
  upsertLink('canonical', canonicalUrl(p));
  upsertRobots('noindex, follow');
  removeJsonLd();
  clearSocialPreview();
}
